import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import BatchChip from '../../components/BatchChip';
import { DetailRow } from '../../components/InvField';
import { getConsumption, getConsumptionItems, confirmConsumption } from '../../api/dispensing';
import { getStores } from '../../api/stockLedger';
import { getUoms } from '../../api/inventoryMaster';
import useItemNames from '../../hooks/useItemNames';
import { storeDisplayName } from '../../utils/storeEntity';
import { normalizeUomList } from '../../utils/masterEntity';
import { isUuidLike } from '../../utils/itemDisplay';
import { captureChargesAfterPatientConfirm } from '../../utils/consumptionChargeCapture';
import { formatConsumptionPatient, resolveConsumptionPatientNames } from '../../utils/consumptionPatient';
import { CONSUMPTION_TYPE_LABELS } from '../../constants/storeManagement';
import { formatDate } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.data || data?.items || []);
}

function buildUomMap(uoms) {
  const map = {};
  (Array.isArray(uoms) ? uoms : []).forEach((u) => {
    const id = u.uom_id || u.id;
    if (id) map[id] = u.uom_code || u.uom_name || u.name || '';
  });
  return map;
}

function lineQuantity(item) {
  return item.requested_quantity ?? item.consumed_quantity ?? item.quantity ?? '—';
}

function resolveUomLabel(item, uomMap, getBaseUomCode) {
  const code = item?.uom_code;
  if (code && !isUuidLike(code)) return code;
  if (item?.uom_name && !isUuidLike(item.uom_name)) return item.uom_name;
  if (item?.uom_id && uomMap[item.uom_id]) return uomMap[item.uom_id];
  return getBaseUomCode?.(item?.item_id) || '—';
}

export default function ConsumptionDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { can } = useInvRole();
  const { resolveItem, getBaseUomCode } = useItemNames();
  const listUrl = url.replace(/\/[^/]+$/, '');

  const [record, setRecord] = useState(null);
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [patientNames, setPatientNames] = useState({});
  const [uomMap, setUomMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmRemarks, setConfirmRemarks] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    // API: GET /inventory/uoms/
    getUoms({ is_active: true, limit: 200 })
      .then(d => setUomMap(buildUomMap(normalizeUomList(d))))
      .catch(() => setUomMap({}));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [row, lineData, storesData] = await Promise.all([
        // API: GET /inventory/dispensing/consumption/{id}/
        getConsumption(id),
        // API: GET /inventory/dispensing/consumption/{id}/items/
        getConsumptionItems(id).catch((e) => {
          console.error('[ConsumptionDetail] load items failed:', e);
          return [];
        }),
        // API: GET /inventory/stores/
        getStores().catch((e) => {
          console.error('[ConsumptionDetail] load stores failed:', e);
          return [];
        }),
      ]);
      setRecord(row);
      setItems(normalizeList(lineData));
      setStores(normalizeList(storesData));
      resolveConsumptionPatientNames([row])
        .then(setPatientNames)
        .catch(() => setPatientNames({}));
    } catch (e) {
      setError(e.message);
      setRecord(null);
      setItems([]);
      setPatientNames({});
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  function resolveStoreName(storeId) {
    if (!storeId) return null;
    const store = stores.find(s => (s.store_id || s.id) === storeId);
    return store ? storeDisplayName(store) : storeId;
  }

  async function doConfirm() {
    if (!can(INV_ACTIONS.CONSUMPTION_CREATE)) return;
    setConfirming(true);
    try {
      // API: POST /inventory/dispensing/consumption/{id}/confirm/
      await confirmConsumption(id, { remarks: confirmRemarks.trim() || undefined });

      if (record?.consumption_type === 'PATIENT') {
        try {
          await captureChargesAfterPatientConfirm({
            ...record,
            transaction_id: record.transaction_id || record.id || id,
            items: items.length ? items : undefined,
          });
          toast.success('Consumption confirmed and billed');
        } catch (chargeErr) {
          toast.warning(
            chargeErr.message
              || 'Consumption confirmed, but pharmacy charge capture failed',
          );
        }
      } else {
        toast.success('Consumption confirmed — stock deducted');
      }

      setConfirmOpen(false);
      setConfirmRemarks('');
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to confirm consumption');
    } finally {
      setConfirming(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <InvButton variant="outlined" tone="teal" onClick={() => history.push(listUrl)}>
          Back to list
        </InvButton>
      </Box>
    );
  }
  if (!record) return null;

  const typeLabel = CONSUMPTION_TYPE_LABELS[record.consumption_type] || record.consumption_type || '—';
  const storeId = record.from_store_id || record.store_id;
  const canConfirm = record.status === 'DRAFT' && can(INV_ACTIONS.CONSUMPTION_CREATE);

  return (
    <Box>
      <PageHeader
        eyebrow="Inventory Management › Consumptions"
        title={record.transaction_number || 'Consumption'}
        actions={(
          <>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.push(listUrl)}>
              Back
            </InvButton>
            {canConfirm && (
              <InvButton
                size="small"
                tone="mint"
                onClick={() => {
                  setConfirmRemarks('');
                  setConfirmOpen(true);
                }}
              >
                Confirm
              </InvButton>
            )}
          </>
        )}
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <SectionCard title="Details" actions={<StatusChip status={record.status} />}>
            <DetailRow label="Type">{typeLabel}</DetailRow>
            <DetailRow label="Store">{resolveStoreName(storeId)}</DetailRow>
            <DetailRow label="Patient">{formatConsumptionPatient(record, patientNames)}</DetailRow>
            <DetailRow label="Admission">{record.admission_id}</DetailRow>
            <DetailRow label="Encounter">{record.encounter_type}</DetailRow>
            <DetailRow label="Priority">{record.priority}</DetailRow>
            <DetailRow label="Billing">{record.billing_status}</DetailRow>
            <DetailRow label="Date">{formatDate(record.transaction_date)}</DetailRow>
            <DetailRow label="Remarks">{record.remarks}</DetailRow>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={7}>
          <SectionCard title="Items Consumed">
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>UOM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0
                    ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary }}>
                          No items
                        </TableCell>
                      </TableRow>
                    )
                    : items.map((item, i) => (
                      <TableRow key={item.line_item_id || item.id || i}>
                        <TableCell>{resolveItem(item)}</TableCell>
                        <TableCell>
                          <BatchChip batchNumber={item.batch_number} expiryDate={item.expiry_date} />
                        </TableCell>
                        <TableCell align="right">{lineQuantity(item)}</TableCell>
                        <TableCell>{resolveUomLabel(item, uomMap, getBaseUomCode)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Consumption"
        description={`Confirm "${record.transaction_number || 'this consumption'}"? Stock will be deducted.`}
        optionalReason
        reasonLabel="Remarks"
        reasonValue={confirmRemarks}
        onReasonChange={setConfirmRemarks}
        loading={confirming}
        onConfirm={doConfirm}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmRemarks('');
        }}
      />
    </Box>
  );
}
